import { CLIENT_PAGES } from '../components/client/constants/pages'

const SHARED_PATHS = {
  root: '/',
  login: '/login',
  logout: '/logout',
  authenticate: '/authenticate',
  authenticateInit: '/authenticate-init',
  notFound: '/404',
  admin: '/admin',
  healthy: '/healthy',
  designSystem: '/design-system',
} as const

const AUTH_PAGE_PATHS = [
  SHARED_PATHS.login,
  SHARED_PATHS.logout,
  SHARED_PATHS.authenticate,
  SHARED_PATHS.authenticateInit,
] as const

function normalizePathname(pathname: string): string {
  const pathEndIndexes = [pathname.indexOf('?'), pathname.indexOf('#')].filter(
    (index) => index !== -1,
  )
  const endIndex = pathEndIndexes.length > 0 ? Math.min(...pathEndIndexes) : pathname.length
  const pathnameOnly = pathname.slice(0, endIndex) || SHARED_PATHS.root
  let trimmedEndIndex = pathnameOnly.length

  while (trimmedEndIndex > 1 && pathnameOnly[trimmedEndIndex - 1] === '/') {
    trimmedEndIndex -= 1
  }

  return pathnameOnly.slice(0, trimmedEndIndex) || SHARED_PATHS.root
}

function isPathOrChildPath(pathname: string, basePath: string): boolean {
  return pathname === basePath || pathname.startsWith(`${basePath}/`)
}

function isTenantLoginPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean)
  return segments.length === 2 && segments[1] === SHARED_PATHS.login.slice(1)
}

function isAuthPagePath(pathname: string): boolean {
  const normalizedPathname = normalizePathname(pathname)

  return (
    AUTH_PAGE_PATHS.some((authPath) => isPathOrChildPath(normalizedPathname, authPath)) ||
    isTenantLoginPath(normalizedPathname)
  )
}

function isDesignSystemPath(pathname: string): boolean {
  return isPathOrChildPath(normalizePathname(pathname), SHARED_PATHS.designSystem)
}

const PATIENT_PATHS = {
  home: '/home',
  welcome: '/welcome',
  hipaaConsent: '/hipaa-consent',
  userProfile: '/user-profile',
  search: '/search',
  kitResults: (kitId: string) => `/kit-results/${kitId}` as const,
  orgLogin: (slug: string) => `/${slug}/login` as const,
  flows: {
    kitRegistration: '/flows/kit-registration',
    kitRegistrationFirstStep: '/flows/kit-registration/kit-id',
    sampleCollection: '/flows/sample-collection',
    step: (flowBaseUrl: string, stepKey: string) => `${flowBaseUrl}/${stepKey}`,
  },
} as const

// Paths that are safe landing targets for patient deep links.
const PATIENT_REDIRECT_ALLOWLIST: string[] = [
  PATIENT_PATHS.home,
  '/kit-results/',
  PATIENT_PATHS.flows.kitRegistration,
  PATIENT_PATHS.flows.sampleCollection,
  PATIENT_PATHS.userProfile,
  PATIENT_PATHS.search,
  PATIENT_PATHS.hipaaConsent,
]

// Paths that must never appear as a patient redirectTo target.
const PATIENT_REDIRECT_DENYLIST: string[] = [
  '/client',
  SHARED_PATHS.admin,
  SHARED_PATHS.login,
  SHARED_PATHS.logout,
  SHARED_PATHS.authenticate,
  PATIENT_PATHS.welcome,
]

const CLIENT_PATHS = {
  base: '/client',
  login: '/client/login',
  dashboard: CLIENT_PAGES.dashboard,
  clientPortalConsent: '/client/client-portal-consent',
  termsOfUse: '/client/terms-of-use',
  notFound: '/client/not-found',
} as const

const CLIENT_PUBLIC_LEGAL_PATHS = [
  CLIENT_PATHS.clientPortalConsent,
  CLIENT_PATHS.termsOfUse,
] as const

// Paths that must never appear as a client redirectTo target.
const CLIENT_REDIRECT_DENYLIST: string[] = [
  CLIENT_PATHS.login,
  CLIENT_PATHS.dashboard,
  CLIENT_PAGES.programDashboard,
  ...CLIENT_PUBLIC_LEGAL_PATHS,
]

export {
  SHARED_PATHS,
  AUTH_PAGE_PATHS,
  isAuthPagePath,
  isDesignSystemPath,
  PATIENT_PATHS,
  PATIENT_REDIRECT_ALLOWLIST,
  PATIENT_REDIRECT_DENYLIST,
  CLIENT_PATHS,
  CLIENT_PUBLIC_LEGAL_PATHS,
  CLIENT_REDIRECT_DENYLIST,
}
